(function () {
  const STORAGE_KEY = 'yamaga_recently_viewed';
  const MAX_ITEMS = 10;

  function normalizeHandle(handle) {
    return String(handle || '').trim().toLowerCase();
  }

  function dedupeRecentlyViewed(items) {
    const seenHandles = new Set();
    const seenIds = new Set();
    const result = [];

    items.forEach((item) => {
      if (!item || !item.handle) return;

      const handle = normalizeHandle(item.handle);
      if (!handle) return;

      const id = item.id != null ? String(item.id) : '';

      if (seenHandles.has(handle)) return;
      if (id && seenIds.has(id)) return;

      seenHandles.add(handle);
      if (id) seenIds.add(id);
      result.push({
        handle: String(item.handle).trim(),
        ...(item.id != null ? { id: item.id } : {}),
      });
    });

    return result;
  }

  function persistRecentlyViewed(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* localStorage unavailable */
    }
  }

  function readRecentlyViewed() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(items)) return [];

      const normalized = dedupeRecentlyViewed(items).slice(0, MAX_ITEMS);

      if (normalized.length !== items.length) {
        persistRecentlyViewed(normalized);
      }

      return normalized;
    } catch (error) {
      return [];
    }
  }

  function saveRecentlyViewed(product) {
    if (!product || !product.handle) return;

    const handle = normalizeHandle(product.handle);
    const productId = product.id != null ? String(product.id) : '';

    const items = dedupeRecentlyViewed([
      { handle: product.handle, ...(product.id != null ? { id: product.id } : {}) },
      ...readRecentlyViewed().filter((item) => {
        if (normalizeHandle(item.handle) === handle) return false;
        if (productId && item.id != null && String(item.id) === productId) return false;
        return true;
      }),
    ]).slice(0, MAX_ITEMS);

    persistRecentlyViewed(items);
  }

  function parseItem(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-recently-viewed-item]');
  }

  function getProductKeyFromNode(node, fallbackHandle) {
    const cardLink = node.querySelector('[id^="CardLink-"]');
    if (cardLink) {
      const id = cardLink.id.split('-').pop();
      if (id) return `id:${id}`;
    }

    const link = node.querySelector('a[href*="/products/"]');
    if (link) {
      try {
        const path = new URL(link.href, window.location.origin).pathname;
        const match = path.match(/\/products\/([^/?#]+)/);
        if (match) return `handle:${normalizeHandle(match[1])}`;
      } catch (error) {
        /* ignore malformed URLs */
      }
    }

    if (fallbackHandle) return `handle:${normalizeHandle(fallbackHandle)}`;
    return null;
  }

  function setSectionVisible(sectionRoot) {
    if (!sectionRoot) return;

    sectionRoot.hidden = false;
    sectionRoot.classList.add('yamaga-recently-viewed-section--visible');
  }

  function updateContentVisibility(component, hasProducts) {
    const emptyNote = component.querySelector('[data-recently-viewed-empty]');
    const listWrapper = component.querySelector('[data-recently-viewed-list-wrapper]');

    if (emptyNote) emptyNote.hidden = hasProducts;
    if (listWrapper) listWrapper.hidden = !hasProducts;
  }

  class YamagaRecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this._renderGeneration = 0;
    }

    connectedCallback() {
      this.render();
    }

    async render() {
      const renderGeneration = ++this._renderGeneration;
      const requestedLimit = parseInt(this.dataset.limit, 10) || MAX_ITEMS;
      const limit = Math.min(requestedLimit, MAX_ITEMS);
      const items = dedupeRecentlyViewed(readRecentlyViewed()).slice(0, limit);
      const sectionRoot = this.closest('.shopify-section');
      const list = this.querySelector('[data-recently-viewed-list]');

      if (this.dataset.designMode === 'true') {
        setSectionVisible(sectionRoot);
        updateContentVisibility(this, false);
        return;
      }

      if (!list) return;

      list.replaceChildren();

      if (!items.length) {
        setSectionVisible(sectionRoot);
        updateContentVisibility(this, false);
        return;
      }

      updateContentVisibility(this, true);

      const root = window.Shopify?.routes?.root || '/';
      const itemSectionId = this.dataset.itemSection || 'yamaga-recently-viewed-item';
      const sectionId = this.dataset.sectionId || 'recently-viewed';

      const responses = await Promise.all(
        items.map(async (item, index) => {
          try {
            const response = await fetch(
              `${root}products/${encodeURIComponent(item.handle)}?section_id=${encodeURIComponent(itemSectionId)}`,
              { credentials: 'same-origin' }
            );
            if (!response.ok) return null;
            const html = await response.text();
            const node = parseItem(html);
            if (!node) return null;
            node.id = `Slide-${sectionId}-${index + 1}`;
            return { index, node, handle: item.handle };
          } catch (error) {
            return null;
          }
        })
      );

      if (renderGeneration !== this._renderGeneration) return;

      const seenProductKeys = new Set();

      responses
        .filter(Boolean)
        .sort((a, b) => a.index - b.index)
        .forEach(({ node, handle }) => {
          const productKey = getProductKeyFromNode(node, handle);
          if (!productKey || seenProductKeys.has(productKey)) return;
          seenProductKeys.add(productKey);
          list.appendChild(document.importNode(node, true));
        });

      setSectionVisible(sectionRoot);
      updateContentVisibility(this, list.children.length > 0);

      if (typeof window.yamagaInitSwymActionButtons === 'function') {
        window.yamagaInitSwymActionButtons(this);
      }
    }
  }

  if (!customElements.get('yamaga-recently-viewed')) {
    customElements.define('yamaga-recently-viewed', YamagaRecentlyViewed);
  }

  function refreshAllRecentlyViewed() {
    document.querySelectorAll('yamaga-recently-viewed').forEach((element) => {
      element.render();
    });
  }

  window.addEventListener('pageshow', refreshAllRecentlyViewed);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      refreshAllRecentlyViewed();
    }
  });

  if (window.yamagaRecentlyViewedProduct) {
    saveRecentlyViewed(window.yamagaRecentlyViewedProduct);
  }
})();
